import { useState, useEffect } from "react"
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Sparkles, Calendar, DollarSign, Users, Target, AlertCircle, FolderKanban, CheckCircle2, Brain } from 'lucide-react'
import { toast } from 'sonner'
import { programsService, ProgramCreate } from '../services/programs'
import { portfoliosService, Portfolio } from '../services/portfolios'
import { usersService, User } from '../services/users'
import { getErrorMessage } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

export default function CreateProgram() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [aiSuggestions, setAiSuggestions] = useState(true)
  const [loading, setLoading] = useState(false)
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [users, setUsers] = useState<User[]>([])
  
  const [formData, setFormData] = useState({
    // Basic Information
    programName: '',
    programCode: '',
    programDescription: '',
    programVision: '',
    businessCase: '',
    scope: '',
    
    // Classification
    portfolioId: '',
    programType: '',
    priority: '',
    strategicAlignment: '',
    riskTolerance: '',
    governanceModel: '',
    complianceRequirements: '',
    
    // Leadership
    programSponsor: '',
    programManager: '',
    programDirector: '',
    
    // Timeline & Scope
    startDate: '',
    endDate: '',
    programPhase: '',
    
    // Budget & Benefits
    totalBudget: '',
    budgetCurrency: 'USD',
    expectedBenefits: '',
    benefitRealization: '',
    programGoals: '',
    successCriteria: '',
    kpis: '',
  })

  useEffect(() => {
    loadInitialData()
    // Auto-generate program code
    if (!formData.programCode) {
      const year = new Date().getFullYear()
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      setFormData(prev => ({ ...prev, programCode: `PRG-${year}-${random}` }))
    }
  }, [])

  const loadInitialData = async () => {
    try {
      const [portfoliosData, usersData] = await Promise.all([
        portfoliosService.list({ status: 'active' }),
        usersService.list()
      ])
      setPortfolios(portfoliosData)
      setUsers(usersData)
    } catch (error) {
      console.error('Error loading initial data:', error)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const steps = [
    { id: 1, name: 'Basic Info', icon: FolderKanban, fields: ['programName', 'programCode', 'programDescription', 'programVision'] },
    { id: 2, name: 'Classification', icon: Target, fields: ['portfolioId', 'programType', 'priority'] },
    { id: 3, name: 'Leadership & Timeline', icon: Users, fields: ['programSponsor', 'programManager', 'startDate', 'endDate'] },
    { id: 4, name: 'Budget & Benefits', icon: DollarSign, fields: ['totalBudget', 'programGoals', 'expectedBenefits'] },
    { id: 5, name: 'Review & Submit', icon: CheckCircle2, fields: [] },
  ]

  const programTypes = [
    'Strategic Initiative',
    'Business Transformation',
    'Technology Modernization',
    'Process Improvement',
    'Product Development',
    'Infrastructure Enhancement',
    'Organizational Change',
  ]

  const priorities = ['Critical', 'High', 'Medium', 'Low']
  const riskTolerances = ['Low', 'Medium', 'High']
  const programPhases = ['Initiation', 'Planning', 'Execution', 'Transition', 'Closure']
  const governanceModels = ['Waterfall', 'Agile', 'Hybrid', 'PRINCE2', 'SAFe']

  const getAiSuggestion = (field: string) => {
    const suggestions: { [key: string]: string } = {
      programDescription: 'AI suggests: Define the overarching purpose, scope boundaries, and how component projects work together to achieve program goals.',
      programVision: 'AI suggests: Articulate the desired future state and transformational outcomes this program will deliver to the organization.',
      businessCase: 'AI suggests: Include strategic drivers, expected ROI, benefit realization timeline, and alignment with portfolio objectives.',
      expectedBenefits: 'AI suggests: Quantify tangible and intangible benefits with specific metrics and target achievement dates.',
      kpis: 'AI suggests: Define leading and lagging indicators that measure program health, progress, and value delivery.',
    }
    return suggestions[field] || ''
  }

  const isStepValid = (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    if (!step) return false
    if (stepId === 5) return true // Review step is always valid

    return step.fields.every(field => {
      const value = formData[field as keyof typeof formData]
      // Required fields check
      if (field === 'programName' || field === 'programCode' || field === 'programDescription' || 
          field === 'programVision' || field === 'portfolioId' || field === 'programType' || 
          field === 'priority' || field === 'programSponsor' || field === 'programManager' || 
          field === 'startDate' || field === 'endDate' || 
          field === 'totalBudget' || field === 'programGoals' || field === 'expectedBenefits') {
        return value && value.toString().trim() !== ''
      }
      return true
    })
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const programData: ProgramCreate = {
        name: formData.programName,
        description: formData.programDescription,
        status: 'planning',
        start_date: formData.startDate || undefined,
        end_date: formData.endDate || undefined,
        portfolio_id: formData.portfolioId ? parseInt(formData.portfolioId) : undefined,
        metadata: {
          programCode: formData.programCode,
          programVision: formData.programVision,
          businessCase: formData.businessCase,
          scope: formData.scope,
          programType: formData.programType,
          priority: formData.priority,
          strategicAlignment: formData.strategicAlignment,
          riskTolerance: formData.riskTolerance,
          governanceModel: formData.governanceModel,
          complianceRequirements: formData.complianceRequirements,
          programSponsor: formData.programSponsor,
          programManager: formData.programManager,
          programDirector: formData.programDirector,
          programPhase: formData.programPhase,
          totalBudget: formData.totalBudget,
          budgetCurrency: formData.budgetCurrency,
          expectedBenefits: formData.expectedBenefits,
          benefitRealization: formData.benefitRealization,
          programGoals: formData.programGoals,
          successCriteria: formData.successCriteria,
          kpis: formData.kpis,
        }
      }

      const program = await programsService.create(programData)
      toast.success('Program created successfully!', {
        description: `${formData.programName} (${formData.programCode}) has been created.`,
      })
      navigate(`/program/${program.id}`)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error creating program:', error)
      toast.error(getErrorMessage(error) || 'Failed to create program')
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h3 className="text-sm mb-1 text-purple-900">AI Program Charter Assistant</h3>
                  <p className="text-sm text-purple-700">
                    I'll guide you through creating a comprehensive program charter aligned with PMI Program Management standards. Programs coordinate multiple related projects to achieve strategic benefits.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Program Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.programName}
                  onChange={(e) => handleInputChange('programName', e.target.value)}
                  placeholder="e.g., Infrastructure Modernization Program"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.programCode}
                      readOnly
                      placeholder="Auto-generating..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Auto-Generated
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Unique sequential identifier (auto-assigned)</p>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Phase
                  </label>
                  <select
                    value={formData.programPhase}
                    onChange={(e) => handleInputChange('programPhase', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select phase...</option>
                    {programPhases.map(phase => (
                      <option key={phase} value={phase}>{phase}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Program Vision Statement <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.programVision}
                  onChange={(e) => handleInputChange('programVision', e.target.value)}
                  placeholder="Describe the desired future state and transformational outcomes..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
                {aiSuggestions && (
                  <p className="text-sm text-purple-600 mt-2 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{getAiSuggestion('programVision')}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Program Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.programDescription}
                  onChange={(e) => handleInputChange('programDescription', e.target.value)}
                  placeholder="Provide a comprehensive description of the program's purpose, scope, and how component projects work together..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
                {aiSuggestions && (
                  <p className="text-sm text-purple-600 mt-2 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{getAiSuggestion('programDescription')}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Business Case & Strategic Justification
                </label>
                <textarea
                  value={formData.businessCase}
                  onChange={(e) => handleInputChange('businessCase', e.target.value)}
                  placeholder="Explain the strategic drivers, expected ROI, benefit realization timeline, and portfolio alignment..."
                  rows={5}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
                {aiSuggestions && (
                  <p className="text-sm text-purple-600 mt-2 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{getAiSuggestion('businessCase')}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Program Scope & Boundaries
                </label>
                <textarea
                  value={formData.scope}
                  onChange={(e) => handleInputChange('scope', e.target.value)}
                  placeholder="Define what is included and explicitly excluded from the program scope..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
              </div>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="text-sm mb-1 text-blue-900">Strategic Classification</h3>
                  <p className="text-sm text-blue-700">
                    Link this program to your portfolio structure and define its strategic positioning within the organization.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Portfolio <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.portfolioId}
                  onChange={(e) => handleInputChange('portfolioId', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                >
                  <option value="">Select portfolio...</option>
                  {portfolios.map(portfolio => (
                    <option key={portfolio.id} value={portfolio.id.toString()}>{portfolio.name}</option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1.5">Programs must be aligned with a portfolio for strategic governance</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.programType}
                    onChange={(e) => handleInputChange('programType', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select type...</option>
                    {programTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select priority...</option>
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Strategic Alignment
                </label>
                <textarea
                  value={formData.strategicAlignment}
                  onChange={(e) => handleInputChange('strategicAlignment', e.target.value)}
                  placeholder="Describe how this program aligns with organizational strategic objectives and portfolio goals..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Risk Tolerance
                  </label>
                  <select
                    value={formData.riskTolerance}
                    onChange={(e) => handleInputChange('riskTolerance', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select risk tolerance...</option>
                    {riskTolerances.map(tolerance => (
                      <option key={tolerance} value={tolerance}>{tolerance}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Governance Model
                  </label>
                  <select
                    value={formData.governanceModel}
                    onChange={(e) => handleInputChange('governanceModel', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select governance model...</option>
                    {governanceModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Compliance & Regulatory Requirements
                </label>
                <input
                  type="text"
                  value={formData.complianceRequirements}
                  onChange={(e) => handleInputChange('complianceRequirements', e.target.value)}
                  placeholder="e.g., SOX, GDPR, HIPAA, ISO 27001, industry-specific regulations"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                />
              </div>
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="text-sm mb-1 text-green-900">Program Leadership & Timeline</h3>
                  <p className="text-sm text-green-700">
                    Assign executive leadership roles and establish the program lifecycle timeline.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Sponsor <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.programSponsor}
                    onChange={(e) => handleInputChange('programSponsor', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select sponsor...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id.toString()}>{user.full_name} - {user.email}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Executive accountable for program success</p>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Manager <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.programManager}
                    onChange={(e) => handleInputChange('programManager', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select program manager...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id.toString()}>{user.full_name} - {user.email}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Responsible for program delivery</p>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Program Director
                  </label>
                  <select
                    value={formData.programDirector}
                    onChange={(e) => handleInputChange('programDirector', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="">Select director (optional)...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id.toString()}>{user.full_name} - {user.email}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Strategic oversight and governance</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  />
                </div>
              </div>

              {formData.startDate && formData.endDate && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-purple-900">
                        Program Duration: {Math.ceil((new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 60 * 60 * 24))} days
                        ({Math.ceil((new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 60 * 60 * 24 * 30))} months)
                      </p>
                      <p className="text-xs text-purple-700 mt-1">
                        Programs typically run 1-3 years and coordinate multiple projects
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="text-sm mb-1 text-amber-900">Budget & Benefit Realization</h3>
                  <p className="text-sm text-amber-700">
                    Define financial parameters and expected benefits. Programs focus on delivering strategic value beyond individual project outcomes.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm mb-2 text-gray-700">
                    Total Program Budget <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.totalBudget}
                    onChange={(e) => handleInputChange('totalBudget', e.target.value)}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Aggregate budget across all component projects</p>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-gray-700">
                    Currency
                  </label>
                  <select
                    value={formData.budgetCurrency}
                    onChange={(e) => handleInputChange('budgetCurrency', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Program Goals & Objectives <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.programGoals}
                  onChange={(e) => handleInputChange('programGoals', e.target.value)}
                  placeholder="List the strategic goals this program aims to achieve (one per line)"
                  rows={5}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Expected Benefits <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.expectedBenefits}
                  onChange={(e) => handleInputChange('expectedBenefits', e.target.value)}
                  placeholder="Quantify tangible and intangible benefits with specific metrics and target achievement dates..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
                {aiSuggestions && (
                  <p className="text-sm text-purple-600 mt-2 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{getAiSuggestion('expectedBenefits')}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Benefit Realization Strategy
                </label>
                <textarea
                  value={formData.benefitRealization}
                  onChange={(e) => handleInputChange('benefitRealization', e.target.value)}
                  placeholder="Describe the approach for tracking, measuring, and realizing expected benefits throughout the program lifecycle..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Success Criteria & KPIs
                </label>
                <textarea
                  value={formData.successCriteria}
                  onChange={(e) => handleInputChange('successCriteria', e.target.value)}
                  placeholder="Define measurable success criteria and key performance indicators..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                />
                {aiSuggestions && (
                  <p className="text-sm text-purple-600 mt-2 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{getAiSuggestion('kpis')}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  KPIs & Metrics
                </label>
                <input
                  type="text"
                  value={formData.kpis}
                  onChange={(e) => handleInputChange('kpis', e.target.value)}
                  placeholder="e.g., ROI > 25%, Time to Market -30%, Customer NPS +40, Cost Reduction $5M"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                />
              </div>
            </div>
          </div>
        )

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600 mt-0.5" />
                <div>
                  <h3 className="text-lg text-green-900">Program Charter Ready for Review</h3>
                  <p className="text-sm text-green-700">
                    Review all information below before submitting. This will create your program charter and initialize all PMI-standard program management artifacts.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Name</h3>
                    <p className="text-base">{formData.programName || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Code</h3>
                    <p className="text-base">{formData.programCode || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Portfolio</h3>
                    <p className="text-base">{portfolios.find(p => p.id.toString() === formData.portfolioId)?.name || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Type</h3>
                    <p className="text-base">{formData.programType || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Priority</h3>
                    <p className="text-base">{formData.priority || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Governance Model</h3>
                    <p className="text-base">{formData.governanceModel || 'Not specified'}</p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Sponsor</h3>
                    <p className="text-base">{users.find(u => u.id.toString() === formData.programSponsor)?.full_name || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Manager</h3>
                    <p className="text-base">{users.find(u => u.id.toString() === formData.programManager)?.full_name || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Timeline</h3>
                    <p className="text-base">
                      {formData.startDate && formData.endDate 
                        ? `${new Date(formData.startDate).toLocaleDateString()} - ${new Date(formData.endDate).toLocaleDateString()}`
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Total Budget</h3>
                    <p className="text-base">
                      {formData.totalBudget 
                        ? `${formData.budgetCurrency} ${Number(formData.totalBudget).toLocaleString()}`
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Risk Tolerance</h3>
                    <p className="text-base">{formData.riskTolerance || 'Not specified'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-500 mb-1">Program Phase</h3>
                    <p className="text-base">{formData.programPhase || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <h3 className="text-sm text-gray-500 mb-2">Program Vision</h3>
                <p className="text-sm text-gray-700">{formData.programVision || 'Not specified'}</p>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <h3 className="text-sm text-gray-500 mb-2">Program Description</h3>
                <p className="text-sm text-gray-700">{formData.programDescription || 'Not specified'}</p>
              </div>

              {formData.programGoals && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <h3 className="text-sm text-gray-500 mb-2">Program Goals</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{formData.programGoals}</p>
                </div>
              )}

              {formData.expectedBenefits && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <h3 className="text-sm text-gray-500 mb-2">Expected Benefits</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{formData.expectedBenefits}</p>
                </div>
              )}
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h3 className="text-sm mb-1 text-purple-900">What happens next?</h3>
                  <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
                    <li>Program charter will be created with all PMI Program Management standard sections</li>
                    <li>Program workspace will be initialized with governance board, benefit tracking, and roadmap</li>
                    <li>Approval workflow will be triggered for executive sponsor review</li>
                    <li>Program team members will receive notifications to join the program</li>
                    <li>Component project structure will be ready for project linking</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/program')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl mb-1">Create New Program</h1>
                <p className="text-sm text-gray-600">PMI Program Management standard charter creation wizard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const newState = !aiSuggestions
                  setAiSuggestions(newState)
                  toast.success(
                    newState 
                      ? '✨ AI Assistance is now ON - Intelligent suggestions will appear as you fill out the form' 
                      : 'AI Assistance is now OFF'
                  )
                }}
                className={`px-4 py-2 rounded-lg border transition-all flex items-center gap-2 ${
                  aiSuggestions 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-600' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                AI Assist {aiSuggestions ? 'On' : 'Off'}
              </button>
              <button
                onClick={() => navigate('/program')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Assist Status Banner */}
      {aiSuggestions && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="max-w-[1400px] mx-auto px-8 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="font-medium">AI Assistance Active</span>
              <span className="text-purple-100 text-sm ml-2">
                - Smart suggestions are shown below each field to help you complete the form
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = currentStep === step.id
              const isCompleted = currentStep > step.id
              const isValid = isStepValid(step.id)

              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                        isCompleted
                          ? 'bg-green-600 text-white'
                          : isActive
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${isActive ? '' : 'text-gray-600'}`}>
                        {step.name}
                      </p>
                      {isActive && !isValid && (
                        <p className="text-xs text-red-600">Required fields missing</p>
                      )}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-3 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-[1000px] mx-auto px-8 py-8">
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className={`px-6 py-2.5 rounded-lg transition-all ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>

            <div className="flex items-center gap-3">
              {currentStep < 5 ? (
                <button
                  onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
                  disabled={!isStepValid(currentStep)}
                  className={`px-6 py-2.5 rounded-lg transition-all ${
                    isStepValid(currentStep)
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next Step
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={loading || !formData.programName}
                  className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Create Program
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
